import dayjs from 'dayjs';
import { BookingDateException, WorkTimeType } from 'shared/types';
import getPrevActiveBusinessDay from 'utils/datetime/schedule/getPrevActiveBusinessDay';

import { getDateFromWorkTime } from '../helpers';

type Props = {
	workTimeAll?: WorkTimeType[];
	delay?: number;
	exceptions?: BookingDateException[];
	// issue on booking is from + delay is next working day, https://www.notion.so/choiceqr/Bookings-minimal-time-interval-before-book-works-incorrect-14417886534380569b12fc26f205037f
	useSameDayHack?: boolean;
};

/**
 * Get Nearest business day from WorkTimeType[], including today,
 * by checking if day is before max till date, otherwise get next available day
 * @important  may not work with days when week start with monday.
 * */
const getNearestBusinessDay = ({
	workTimeAll,
	exceptions,
	delay = 0,
	useSameDayHack,
}: Props): Date => {
	const isThereNoWorkTime = !workTimeAll || !workTimeAll.length;

	let dateWithDelay = dayjs().add(delay, 'minutes');
	const prevBusinessSchedule = getPrevActiveBusinessDay({
		date: dateWithDelay,
		workTimeAll: workTimeAll ?? [],
		delay,
	});
	const isDateStillInPrevBusinessDay = dateWithDelay.isBefore(
		prevBusinessSchedule.dateWorkTime.tillDelayed
	);

	let startDate = isDateStillInPrevBusinessDay ? prevBusinessSchedule.day : dateWithDelay;

	if (isThereNoWorkTime) return dateWithDelay.toDate();
	const currentWeekDay = startDate.day();
	let workTime = workTimeAll.find((w) => w.dayOfWeek === currentWeekDay);

	if (!workTime) {
		do {
			startDate = startDate.add(1, 'day');
			workTime = workTimeAll.find((w) => w.dayOfWeek === startDate.day());
		} while (!workTime?.active && startDate.day() !== currentWeekDay);

		return startDate.toDate();
	}

	const { tillDelayed, fromDelayed, from } = getDateFromWorkTime({
		workTime: workTime,
		date: startDate.toDate(),
		delay,
	});

	const isAvailableToday = dateWithDelay.isBefore(tillDelayed);
	const isAvailableNow = dateWithDelay.isAfter(fromDelayed);

	if (workTime.active && isAvailableToday && isAvailableNow) {
		return dateWithDelay.toDate();
	}

	if (workTime.active && isAvailableToday) {
		if (useSameDayHack) {
			if (dayjs(fromDelayed).isSame(dateWithDelay, 'date')) return fromDelayed;
			return from;
		}
		return fromDelayed;
	}

	do {
		dateWithDelay = dateWithDelay.add(1, 'day');
		workTime = workTimeAll.find((w) => w.dayOfWeek === dateWithDelay.day());
	} while (!workTime?.active && dateWithDelay.day() !== currentWeekDay);
	const exceptionsSmallestDate = _bookingExceptions({ date: dateWithDelay, exceptions });

	if (exceptionsSmallestDate) return exceptionsSmallestDate.toDate();

	return getDateFromWorkTime({ workTime: workTime!, date: dateWithDelay.toDate(), delay })
		.fromDelayed!;
};

/**
 * check if date is available excluding break for that day.
 * @important breaks functional is in WIP, may change after time
 * */
const _checkDayAvailableDueToWorkTimeBreaks = ({
	dateToCheck,
	workTime,
}: {
	workTime: WorkTimeType;
	dateToCheck: Date;
}): boolean => {
	const breaks = workTime.breaks;

	if (!breaks) return true;

	const { from, till } = getDateFromWorkTime({ workTime, date: dateToCheck });
	for (const i of breaks) {
		const breakRange = getDateFromWorkTime({ workTime: i, date: dateToCheck });
		if (dateToCheck >= breakRange.from && dateToCheck <= breakRange.till) {
			// Date falls within a break, so it is not available
			return false;
		}
	}

	// Date is outside all breaks, check if it falls within the specified range
	return dateToCheck >= from && dateToCheck <= till;
};

/**
 * Booking exceptions are valid only for BookingDateException[]
 * */
const _bookingExceptions = ({
	date,
	exceptions,
}: {
	date: dayjs.Dayjs;
	exceptions?: BookingDateException[];
}) => {
	if (!exceptions) return;
	if (exceptions?.find((e) => dayjs(e.date).isSame(date, 'date'))) return;
	const today = dayjs();

	const smallestDate = exceptions
		?.filter((e) => dayjs(e.date).isBefore(today) || dayjs(e.date).isSame(today, 'date'))
		.reduce(
			(prev, current) => (dayjs(current.date).isAfter(prev, 'date') ? dayjs(current.date) : prev),
			date
		);

	if (smallestDate) return smallestDate;
};

export default getNearestBusinessDay;
